import React, { useState } from "react";
import type { BikeImpressions } from "../../types/impressionJob";
import { ChevronDown, ChevronRight } from "lucide-react";

interface JobAnalyticsTableProps {
  analytics: BikeImpressions[];
}

export const JobAnalyticsTable: React.FC<JobAnalyticsTableProps> = ({
  analytics,
}) => {
  const [expandedBikes, setExpandedBikes] = useState<Set<string>>(new Set());

  const toggleBike = (bikeId: string) => {
    const newExpanded = new Set(expandedBikes);
    if (newExpanded.has(bikeId)) {
      newExpanded.delete(bikeId);
    } else {
      newExpanded.add(bikeId);
    }
    setExpandedBikes(newExpanded);
  };

  return (
    <div className="space-y-4">
      {analytics.map((bike) => {
        const isExpanded = expandedBikes.has(bike.bike_id);
        const totalImpressions = bike.daily_impressions.reduce(
          (sum, day) => sum + day.total_impressions,
          0,
        );

        return (
          <div
            key={bike.bike_id}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Bike Header */}
            <button
              onClick={() => toggleBike(bike.bike_id)}
              className="w-full bg-gray-50 px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
                <span className="font-semibold text-gray-900">
                  Bike ID: {bike.bike_id}
                </span>
                <span className="text-sm text-gray-500">
                  ({bike.daily_impressions.length} days)
                </span>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Impressions</div>
                <div className="text-lg font-bold text-blue-600">
                  {totalImpressions.toLocaleString()}
                </div>
              </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Impressions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hourly Breakdown
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Route Points
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {bike.daily_impressions.map((day) => (
                      <tr key={day.date} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {new Date(day.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {day.total_impressions.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex flex-wrap gap-1">
                            {day.hourly_impressions
                              .filter((hour) => hour.impressions > 0)
                              .map((hour) => (
                                <span
                                  key={hour.hour}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                                  title={`${
                                    hour.hour
                                  }: ${hour.impressions.toLocaleString()} impressions`}
                                >
                                  {hour.hour.split("-")[0]}: {hour.impressions}
                                </span>
                              ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {day.route_length} points
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
